/**
 * Hook that deletes all contract extraction data for a player and
 * document type, then refetches the extraction query so the UI
 * updates.
 *
 * The document is defined inline via `gql` rather than imported from
 * `generated/graphql.ts` so the hook can ship before the codegen step
 * runs against the updated schema.
 * @module features/contracts/hooks/useDeleteContractExtraction
 */

import { gql, useMutation } from "@apollo/client";
import { useCallback, useMemo, useState } from "react";

import { activeContractExtractionRefetch } from "./contractExtractionRefetch";

/** GraphQL document for the DeleteContractExtraction mutation. */
export const DELETE_CONTRACT_EXTRACTION_MUTATION = gql`
  mutation DeleteContractExtraction(
    $input: DeleteContractExtractionInput!
    $confirmation: String!
  ) {
    deleteContractExtraction(input: $input, confirmation: $confirmation)
  }
`;

/** Shape of the DeleteContractExtraction mutation response. */
interface DeleteContractExtractionResponse {
  readonly deleteContractExtraction: boolean;
}

/** Shape of the DeleteContractExtraction mutation variables. */
interface DeleteContractExtractionVariables {
  readonly input: {
    readonly playerId: string;
    readonly documentType: string;
  };
  readonly confirmation: string;
}

/** Public API returned by `useDeleteContractExtraction`. */
export interface UseDeleteContractExtractionResult {
  /** Deletes extraction data and refetches the extraction query on success. */
  readonly deleteExtraction: (
    playerId: string,
    documentType: string,
    confirmation: string
  ) => Promise<boolean>;
  /** Whether a delete request is currently in flight. */
  readonly isDeleting: boolean;
}

/**
 * Wraps the DeleteContractExtraction mutation with loading state and
 * automatic refetching of the extraction query on success.
 * @param options - Hook options
 * @param options.onCompleted - Optional callback invoked after successful deletion
 * @returns Imperative delete function + loading state
 */
export function useDeleteContractExtraction(options?: {
  readonly onCompleted?: () => void;
}): UseDeleteContractExtractionResult {
  const [runMutation] = useMutation<
    DeleteContractExtractionResponse,
    DeleteContractExtractionVariables
  >(DELETE_CONTRACT_EXTRACTION_MUTATION);

  const [isDeleting, setIsDeleting] = useState(false);

  const deleteExtraction = useCallback(
    async (
      playerId: string,
      documentType: string,
      confirmation: string
    ): Promise<boolean> => {
      setIsDeleting(true);
      try {
        const { data } = await runMutation({
          variables: {
            input: { playerId, documentType },
            confirmation,
          },
          refetchQueries: [
            activeContractExtractionRefetch(playerId, documentType),
          ],
          awaitRefetchQueries: true,
        });
        const didDelete = data?.deleteContractExtraction === true;
        if (didDelete) {
          options?.onCompleted?.();
        }
        return didDelete;
      } catch {
        return false;
      } finally {
        setIsDeleting(false);
      }
    },
    [runMutation, options]
  );

  return useMemo(
    () => ({ deleteExtraction, isDeleting }),
    [deleteExtraction, isDeleting]
  );
}
